import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Activity, Volume2, VolumeX } from "lucide-react";
import { api } from "../lib/api";
import { useTenantSocket } from "../lib/ws";
import { useI18n } from "../lib/i18n";
import { LanguageToggle } from "../components/Brand";

// Gentle two-tone chime via WebAudio
function useChime() {
  const ctxRef = useRef(null);
  return () => {
    try {
      if (!ctxRef.current) {
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = ctxRef.current;
      const now = ctx.currentTime;
      [[880, 0.0, 0.25], [1320, 0.2, 0.45]].forEach(([freq, t0, dur]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0, now + t0);
        g.gain.linearRampToValueAtTime(0.18, now + t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, now + t0 + dur);
        o.connect(g).connect(ctx.destination);
        o.start(now + t0);
        o.stop(now + t0 + dur + 0.05);
      });
    } catch { /* ignore */ }
  };
}

export default function Display() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [state, setState] = useState({ tenant: null, now_serving: null, recent_called: [], waiting: [] });
  const [clock, setClock] = useState(new Date());
  const [soundOn, setSoundOn] = useState(true);
  const [flash, setFlash] = useState(false);
  const playChime = useChime();
  const soundOnRef = useRef(soundOn);
  soundOnRef.current = soundOn;

  const load = async () => {
    const { data } = await api.get(`/public/tenant/${slug}/display`);
    setState(data);
  };

  useEffect(() => { load(); }, [slug]);
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useTenantSocket(slug, (msg) => {
    if (!msg || !msg.type) return;
    if (["ticket.called", "ticket.recalled", "ticket.done", "ticket.absent", "ticket.transferred", "ticket.created"].includes(msg.type)) {
      load();
    }
    if (msg.type === "ticket.called" || msg.type === "ticket.recalled") {
      setFlash(true);
      setTimeout(() => setFlash(false), 1600);
      if (soundOnRef.current) playChime();
    }
  });

  const timeStr = useMemo(() => clock.toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-US", { hour: "2-digit", minute: "2-digit" }), [clock, lang]);
  const dateStr = useMemo(() => clock.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { weekday: "long", day: "2-digit", month: "long" }), [clock, lang]);

  const serving = state.now_serving;

  const enableSound = () => {
    setSoundOn(true);
    // trigger unlock on mobile safari
    playChime();
  };

  return (
    <div className={`min-h-screen bg-slate-100 text-slate-900 p-6 sm:p-10 transition ${flash ? "ring-8 ring-blue-500/30" : ""}`}>
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-700 text-white grid place-items-center">
            <Activity className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-sm uppercase tracking-[0.25em] text-blue-700 font-medium">{t("display")}</div>
            <div className="font-heading text-2xl sm:text-3xl font-bold">{state.tenant?.name || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-heading text-3xl sm:text-4xl font-bold">{timeStr}</div>
            <div className="text-sm text-slate-500 capitalize">{dateStr}</div>
          </div>
          <button
            onClick={() => (soundOn ? setSoundOn(false) : enableSound())}
            data-testid="display-sound-toggle"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            {soundOn ? <Volume2 className="w-4 h-4 text-blue-700" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <LanguageToggle />
        </div>
      </header>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-8 sm:p-12">
          <div className="text-sm uppercase tracking-[0.3em] text-slate-500">{t("now_serving")}</div>
          {serving ? (
            <div className="mt-4 fade-in-up" data-testid="display-now-serving">
              <div
                className="font-heading font-extrabold text-slate-900 leading-none tracking-tighter"
                style={{ fontSize: "clamp(5rem, 14vw, 14rem)" }}
              >
                {serving.number}
              </div>
              <div className="mt-6 font-heading text-4xl sm:text-6xl font-semibold text-slate-600">
                {t("room")} · {serving.counter_name || "—"}
              </div>
              <div className="mt-3 text-lg text-slate-500">
                {lang === "fr" ? serving.service_name_fr : serving.service_name_en}
              </div>
            </div>
          ) : (
            <div className="mt-10 text-3xl text-slate-400 font-heading">—</div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6">
            <div className="text-sm uppercase tracking-[0.3em] text-slate-500">{t("next_up")}</div>
            <div className="mt-4 space-y-2" data-testid="display-waiting-list">
              {state.waiting.slice(0, 6).map((w) => (
                <div key={w.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="font-heading text-2xl font-bold text-slate-900">{w.number}</div>
                  <div className="text-sm text-slate-500">{lang === "fr" ? w.service_name_fr : w.service_name_en}</div>
                </div>
              ))}
              {state.waiting.length === 0 && (
                <div className="text-slate-400 text-sm">{t("no_patient_waiting")}</div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-6">
            <div className="text-sm uppercase tracking-[0.3em] text-slate-500">{t("called")}</div>
            <div className="mt-4 space-y-2">
              {state.recent_called.slice(1, 5).map((w) => (
                <div key={w.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="font-heading text-xl font-semibold text-slate-900">{w.number}</div>
                  <div className="text-sm text-slate-500">{w.counter_name || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 overflow-hidden rounded-full bg-white border border-slate-200">
        <div className="flex whitespace-nowrap animate-marquee py-3 text-slate-600 font-heading text-lg">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 px-6">
              <span>· FilePro — {state.tenant?.name || ""}</span>
              <span>· {t("waiting")}: {state.waiting.length} {t("patients")}</span>
              <span>· {t("please_wait_called")}</span>
              <span>· Clinique Santé Plus · +221 33 000 00 00</span>
              <span>· Urgence prioritaire · Priorité automatique</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
